import { Site, SiteAggregateData } from '@domain/graphql.types';
import { ExpandMore, InsertChartOutlined } from '@mui/icons-material';
import { Box, ButtonBase, Stack, Typography } from '@mui/material';
import { useContext, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HistogramBucket } from '../components/HistoricalViewContainer';
import { median, StationPriceSeries, withMiddleBand } from '../components/priceBands';
import { WeeklyStationCharts } from '../components/StationCharts';
import SiteLookupContext from '../context/SiteLookupContext';
import { SiteLookupDataViewValue } from '../siteLookupDataView';
import { siteLookupTokens } from '../siteLookupTokens';

const { color } = siteLookupTokens;

type Props = {
  sites: readonly Site[];
  aggregateData?: SiteAggregateData | null;
};

type OverviewData = {
  numSites: number;
  numSitesTracked: number;
  numPorts: number;
  numPortsTracked: number;
  medianPrice: number | undefined;
};

/** "Area Summary": a distinct card whose button discloses the summary of the filtered stations, before the report cap. */
const OverviewCard = ({ sites, aggregateData }: Props) => {
  const { t } = useTranslation();
  const { dataView } = useContext(SiteLookupContext);
  const [isOpen, setIsOpen] = useState(false);
  const contentId = useId();

  const overviewData: OverviewData = useMemo(() => computeOverviewData(sites), [sites]);

  function isAllowedPricingModel(pricingModel: string | undefined): boolean {
    const ALLOWED_PRICING_MODELS = new Set(['DYNAMIC', 'FIXED', 'TOU', 'POWER']);
    return !!pricingModel && ALLOWED_PRICING_MODELS.has(pricingModel);
  }

  // The sites are the filtered set returned by the search (before the report cap): ports are the matching ones.
  function computeOverviewData(sites: readonly Site[]): OverviewData {
    return {
      numSites: sites.length,
      numSitesTracked: sites.filter((site) => site.trackingState === 'TRACKED').length,
      numPorts: sites.reduce((acc, site) => acc + (site.matchingPortsCount || 0), 0),
      numPortsTracked: sites.filter((site) => site.trackingState === 'TRACKED').reduce((acc, site) => acc + (site.matchingPortsCount || 0), 0),
      medianPrice: computeMedianPrice(sites),
    };
  }

  // Same charts as the station sheet, fed with the buckets aggregated over the filtered stations; the median line
  // and the middle 50% come from the stations' own weekly prices, the API has no percentile.
  const aggregateHistogramBuckets = useMemo(() => {
    const buckets: HistogramBucket[] = (aggregateData?.buckets ?? []).map((b) => ({
      endDate: String(b.endDate),
      percentageUtilization: b.percentageUtilization ?? undefined,
      price: b.price ?? undefined,
      minPrice: b.minPrice ?? undefined,
      maxPrice: b.maxPrice ?? undefined,
    }));
    const stationPrices: StationPriceSeries[] = sites.map((site) => site.historicalView?.utilizationSeries?.buckets ?? []);
    return withMiddleBand(buckets, stationPrices);
  }, [aggregateData, sites]);

  // The charts' own empty state speaks of "this station": without any value, the counts above are enough.
  const hasChartData = aggregateHistogramBuckets.some((b) => b.percentageUtilization !== undefined || b.medianPrice !== undefined);

  // Median of each station's latest weekly price.
  function computeMedianPrice(sites: readonly Site[]): number | undefined {
    const latestPrices = sites.map((site) => {
      const utilizationBuckets = site.historicalView?.utilizationSeries?.buckets;
      if (
        !site.historicalView ||
        !isAllowedPricingModel(site.historicalView.pricingModel ?? undefined) ||
        !utilizationBuckets ||
        utilizationBuckets.length === 0
      ) {
        return undefined;
      }
      return utilizationBuckets[utilizationBuckets.length - 1].price;
    });

    return median(latestPrices.filter((p): p is number => typeof p === 'number' && Number.isFinite(p))) ?? undefined;
  }

  return (
    <Box sx={{ backgroundColor: color.areaSummary, borderRadius: '10px' }}>
      <ButtonBase
        aria-expanded={isOpen}
        aria-controls={contentId}
        onClick={() => setIsOpen((open) => !open)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          width: '100%',
          minHeight: 48,
          paddingX: 1.5,
          borderRadius: '10px',
          textAlign: 'left',
          '&.Mui-focusVisible': { outline: `2px solid ${color.primary}`, outlineOffset: -2 },
        }}
      >
        <InsertChartOutlined aria-hidden fontSize="small" sx={{ color: color.primary }} />
        <Typography flex={1} fontSize={14} fontWeight={600} color={color.text.primary}>
          {t('siteLookup.overview.areasummary')}
        </Typography>
        <ExpandMore
          aria-hidden
          fontSize="small"
          sx={{ color: color.text.secondary, transform: isOpen ? 'rotate(180deg)' : 'rotate(-90deg)', transition: 'transform 150ms' }}
        />
      </ButtonBase>

      <Box id={contentId} hidden={!isOpen} paddingX={1.5} paddingBottom={1.5}>
        {isOpen &&
          (!sites.length ? (
            <Typography color={color.text.secondary} fontSize={14}>
              {t('siteLookup.overview.empty')}
            </Typography>
          ) : (
            <Stack gap={1}>
              <Typography component="div" fontSize={14} color={color.text.primary}>
                <Box>
                  <strong>{t('siteLookup.overview.stationsCountTitle')}</strong>
                  {t('siteLookup.overview.stationsCount', { numSites: overviewData.numSites, numSitesTracked: overviewData.numSitesTracked })}
                </Box>
                <Box>
                  <strong>{t('siteLookup.overview.portsCountTitle')}</strong>
                  {t('siteLookup.overview.portsCount', { numPorts: overviewData.numPorts, numPortsTracked: overviewData.numPortsTracked })}
                </Box>
                {/* Real-time loads no price history: the median would always read "$-". */}
                {dataView !== SiteLookupDataViewValue.realtime && (
                  <Box>
                    <strong>{t('siteLookup.overview.medianPriceTitle')}</strong>{' '}
                    {t('siteLookup.overview.medianPrice', { medianPrice: overviewData.medianPrice !== undefined ? overviewData.medianPrice.toFixed(2) : '-' })}
                  </Box>
                )}
              </Typography>
              {hasChartData && <WeeklyStationCharts buckets={aggregateHistogramBuckets} showPriceSpread />}
            </Stack>
          ))}
      </Box>
    </Box>
  );
};

export default OverviewCard;
